import apiEndpoints from 'api/endpoints';

import { baseAPI } from './baseApi';

export const settingsApi = baseAPI.injectEndpoints({
    endpoints: (builder) => ({
        toggleWebhookAuthToken: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.setWebhookAuthToken,
                method: 'PUT',
                body: payload,
            }),
        }),
        fetchWebhookAuthToken: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.getWebhookAuthToken,
                method: 'POST',
                body: payload,
            }),
        }),
        fetchCustomSenderEmail: builder.mutation({
            query: (payload) => ({
                url: `${apiEndpoints.settings.customSenderEmail.default}/?companyUUID=${payload.companyUUID}`,
                method: 'GET',
                // Bypass the browser HTTP cache so a removed/changed email (or a
                // different admin's value on the same machine) is never served stale.
                cache: 'no-store',
            }),
        }),
        resendVerificationEmail: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.settings.customSenderEmail.resendVerificationEmail,
                method: 'POST',
                body: payload,
            }),
        }),
        modifyCustomSenderEmail: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.settings.customSenderEmail.default,
                method: payload.method,
                body: payload.data,
            }),
        }),
        removeCustomSenderEmail: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.settings.customSenderEmail.default,
                method: 'DELETE',
                body: payload,
            }),
        }),
        changeAccountPassword: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.updatePassword,
                method: 'POST',
                body: payload,
            }),
        }),
        modifyMedallionNotificationStatus: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.settings.email,
                method: 'POST',
                body: payload,
            }),
        }),
        fetchCompanyWebhook: builder.query({
            query: () => ({
                url: apiEndpoints.addWebhookUrl,
                method: 'GET',
            }),
        }),
        modifyCompanyWebhook: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.addWebhookUrl,
                method: 'POST',
                body: payload,
            }),
        }),
        fetchCompanyApiKey: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.accessCode,
                method: 'POST',
                body: payload,
            }),
        }),
        fetchCompanyAdmins: builder.query({
            query: (payload) => ({
                url: apiEndpoints.getAdmins,
                method: 'POST',
                body: payload,
            }),
            providesTags: ['CompanyAdmins'],
        }),
        createCompanyMember: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.createNewMember,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['CompanyAdmins'],
        }),
        updateCompanyMember: builder.mutation({
            query: (payload) => ({
                url: `admin/company/admin-account/${payload.uac}/update`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['CompanyAdmins'],
        }),
        resendCompanyMemberInvitation: builder.mutation({
            query: (payload) => ({
                url: `admin/company/admin-account/${payload.uac}/resend-invite`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['CompanyAdmins'],
        }),
        reActivateCompanyMember: builder.mutation({
            query: (payload) => ({
                url: `admin/company/admin-account/${payload.uac}/re-activate`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['CompanyAdmins'],
        }),
        changeCompanyMemberRole: builder.mutation({
            query: (payload) => ({
                url: `admin/company/admin-account/${payload.uac}/change-role`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['CompanyAdmins'],
        }),
        suspendCompanyMember: builder.mutation({
            query: (payload) => ({
                url: `admin/company/admin-account/${payload.uac}/suspend`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['CompanyAdmins'],
        }),

        addEmailRecipients: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.settings.emailRecipients,
                method: 'POST',
                body: payload,
            }),
        }),
        fetchMultipleWebhooks: builder.query({
            query: () => ({
                url: apiEndpoints.settings.webhooks,
                method: 'GET',
                // Bypass the browser HTTP cache so a webhook added via API or in
                // another browser is never served stale on refresh.
                cache: 'no-store',
            }),
        }),
        fetchWebhookLogs: builder.query({
            query: (params) => ({
                url: apiEndpoints.settings.webhookLogs,
                method: 'GET',
                params,
                cache: 'no-store',
            }),
        }),
        fetchWebhookLogDetail: builder.query({
            query: (id) => ({
                url: `${apiEndpoints.settings.webhookLogs}/${id}`,
                method: 'GET',
                cache: 'no-store',
            }),
        }),
        createWebhook: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.addWebhookUrl,
                method: 'POST',
                body: payload,
            }),
        }),
        updateWebhook: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `${apiEndpoints.addWebhookUrl}/${id}`,
                method: 'PUT',
                body,
            }),
        }),
        deleteWebhook: builder.mutation({
            query: (id) => ({
                url: `${apiEndpoints.addWebhookUrl}/${id}`,
                method: 'DELETE',
            }),
        }),
        fetchWebhookToken: builder.mutation({
            query: ({ id, password }) => ({
                url: `${apiEndpoints.addWebhookUrl}/active/token/${id}`,
                method: 'POST',
                body: { password },
            }),
        }),
        regenerateWebhookToken: builder.mutation({
            query: (id) => ({
                url: `${apiEndpoints.regenerateWebhookToken}/${id}`,
                method: 'POST',
            }),
        }),
    }),
});

export const {
    useFetchWebhookAuthTokenMutation: useFetchWebhookAuthToken,
    useToggleWebhookAuthTokenMutation: useToggleWebhookAuthToken,
    useFetchCustomSenderEmailMutation: useFetchCustomSenderEmail,
    useResendVerificationEmailMutation: useResendVerificationEmail,
    useModifyCustomSenderEmailMutation: useModifyCustomSenderEmail,
    useRemoveCustomSenderEmailMutation: useRemoveCustomSenderEmail,
    useChangeAccountPasswordMutation: useChangeAccountPassword,
    useModifyMedallionNotificationStatusMutation: useModifyMedallionNotificationStatus,
    useFetchCompanyWebhookQuery: useFetchCompanyWebhook,
    useModifyCompanyWebhookMutation: useModifyCompanyWebhook,
    useFetchCompanyApiKeyMutation: useFetchCompanyApiKey,
    useFetchCompanyAdminsQuery: useFetchCompanyAdmins,
    useCreateCompanyMemberMutation: useCreateCompanyMember,
    useUpdateCompanyMemberMutation: useUpdateCompanyMember,
    useResendCompanyMemberInvitationMutation: useResendCompanyMemberInvitation,
    useReActivateCompanyMemberMutation: useReActivateCompanyMember,
    useChangeCompanyMemberRoleMutation: useChangeCompanyMemberRole,
    useSuspendCompanyMemberMutation: useSuspendCompanyMember,
    useAddEmailRecipientsMutation: useAddEmailRecipients,
    useFetchMultipleWebhooksQuery: useFetchMultipleWebhooks,
    useFetchWebhookLogsQuery: useFetchWebhookLogs,
    useFetchWebhookLogDetailQuery: useFetchWebhookLogDetail,
    useCreateWebhookMutation: useCreateWebhook,
    useUpdateWebhookMutation: useUpdateWebhook,
    useDeleteWebhookMutation: useDeleteWebhook,
    useFetchWebhookTokenMutation: useFetchWebhookToken,
    useRegenerateWebhookTokenMutation: useRegenerateWebhookToken,
} = settingsApi;

export default settingsApi.middleware;
